import Phaser from 'phaser';
import {
  WALL_X, WALL_WIDTH, WALL_Y_START, WALL_Y_END,
  WALL_BASE_HP, WALL_SLOT_COUNT, WALL_HP_BASE, SCALING,
} from '../config';
import { SlotPosition } from '../types';
import { achievementManager } from '../managers/AchievementManager';
import { gameStateManager } from '../managers/GameStateManager';

export class Wall {
  scene: Phaser.Scene;
  maxHP: number;
  currentHP: number;
  upgradeLevel: number;
  slots: SlotPosition[];
  graphics: Phaser.GameObjects.Graphics;

  // Permanent tracking via gameStateManager
  get totalDamageTaken(): number { return gameStateManager.permanent.totalWallDamageTaken; }
  set totalDamageTaken(v: number) { gameStateManager.permanent.totalWallDamageTaken = v; }
  get totalRepaired(): number { return gameStateManager.permanent.totalHPRepaired; }
  set totalRepaired(v: number) { gameStateManager.permanent.totalHPRepaired = v; }

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.upgradeLevel = 0;
    const wallHPBonus = achievementManager.getBonuses().wallHP;
    const elderBonuses = (scene as any).elderBonuses;
    const elderWallMult = elderBonuses?.wallHPMult ?? 0;
    this.maxHP = Math.round(WALL_BASE_HP * (1 + wallHPBonus) * (1 + elderWallMult));
    this.currentHP = this.maxHP;
    this.graphics = scene.add.graphics();

    // Wall slots along the wall (right side, where defenders stand)
    this.slots = [];
    const activeSlotCount = elderBonuses?.unlockBoomerangThrower ? WALL_SLOT_COUNT + 1 : WALL_SLOT_COUNT;
    const slotSpacing = (WALL_Y_END - WALL_Y_START) / (activeSlotCount + 1);
    for (let i = 0; i < activeSlotCount; i++) {
      this.slots.push({
        // Place precisely in the horizontal center of the wall
        x: WALL_X + (WALL_WIDTH / 2) + 4,
        y: WALL_Y_START + slotSpacing * (i + 1),
        occupied: false,
      });
    }

    this.draw();
  }

  takeDamage(amount: number): void {
    const before = this.currentHP;
    this.currentHP = Math.max(0, this.currentHP - amount);
    this.totalDamageTaken += before - this.currentHP;
    this.draw();
  }

  repair(amount: number): void {
    const before = this.currentHP;
    this.currentHP = Math.min(this.maxHP, this.currentHP + amount);
    this.totalRepaired += this.currentHP - before;
    this.draw();
  }

  upgrade(): void {
    this.upgradeLevel++;
    const wallHPBonus = achievementManager.getBonuses().wallHP;
    const elderWallMult = (this.scene as any).elderBonuses?.wallHPMult ?? 0;
    this.maxHP = Math.round(WALL_HP_BASE * Math.pow(SCALING.wallHpGrowth, this.upgradeLevel) * (1 + wallHPBonus) * (1 + elderWallMult));
    this.draw();
  }

  addMaxHP(bonus: number): void {
    this.maxHP += bonus;
    this.currentHP += bonus;
    this.draw();
  }

  recalculateMaxHP(): void {
    const oldMax = this.maxHP;
    const wallHPBonus = achievementManager.getBonuses().wallHP;
    const elderWallMult = (this.scene as any).elderBonuses?.wallHPMult ?? 0;
    this.maxHP = Math.round(WALL_HP_BASE * Math.pow(SCALING.wallHpGrowth, this.upgradeLevel) * (1 + wallHPBonus) * (1 + elderWallMult));

    // If max increased, heal for the difference
    if (this.maxHP > oldMax) {
      this.currentHP += (this.maxHP - oldMax);
    }
    // If max decreased (unlikely), clamp current
    if (this.currentHP > this.maxHP) {
      this.currentHP = this.maxHP;
    }
    this.draw();
  }

  resetHP(): void {
    this.currentHP = this.maxHP;
    this.draw();
  }

  reset(): void {
    this.upgradeLevel = 0;
    const wallHPBonus = achievementManager.getBonuses().wallHP;
    const elderBonuses = (this.scene as any).elderBonuses;
    const elderWallMult = elderBonuses?.wallHPMult ?? 0;
    this.maxHP = Math.round(WALL_BASE_HP * (1 + wallHPBonus) * (1 + elderWallMult));
    this.currentHP = this.maxHP;

    // Re-calculate slots in case prestige unlocked the boomerang
    this.slots = [];
    const activeSlotCount = elderBonuses?.unlockBoomerangThrower ? WALL_SLOT_COUNT + 1 : WALL_SLOT_COUNT;
    const slotSpacing = (WALL_Y_END - WALL_Y_START) / (activeSlotCount + 1);
    for (let i = 0; i < activeSlotCount; i++) {
      this.slots.push({
        // Place precisely in the horizontal center of the wall
        x: WALL_X + (WALL_WIDTH / 2) + 4,
        y: WALL_Y_START + slotSpacing * (i + 1),
        occupied: false,
      });
    }

    this.draw();
  }

  isDestroyed(): boolean {
    return this.currentHP <= 0;
  }

  getHPPercent(): number {
    return this.currentHP / this.maxHP;
  }

  getNextFreeSlot(): SlotPosition | null {
    return this.slots.find(s => !s.occupied) || null;
  }

  // A simple seeded random function so damage decals look consistent frame-to-frame
  private seededRandom(seed: number): number {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  }

  draw(): void {
    this.graphics.clear();
    const hpPct = this.getHPPercent();
    const wallHeight = WALL_Y_END - WALL_Y_START;
    const wallWidth = WALL_WIDTH + 16;
    const wallLeft = WALL_X - 4;

    // --- BASE WALL COLOR ---
    // Premium stone look. Healthy wall is a clean cool grey.
    // As it takes damage, it blends towards a darker, dirtier dusty brown.
    const healthyR = 0x8a, healthyG = 0x8c, healthyB = 0x93; // Cool grey slate
    const ruinedR = 0x5a, ruinedG = 0x4d, ruinedB = 0x40;    // Dark dusty brown

    const r = Math.round(ruinedR + (healthyR - ruinedR) * hpPct);
    const g = Math.round(ruinedG + (healthyG - ruinedG) * hpPct);
    const b = Math.round(ruinedB + (healthyB - ruinedB) * hpPct);
    const wallColor = (r << 16) | (g << 8) | b;

    // Base Fill
    this.graphics.fillStyle(wallColor, 1);
    this.graphics.fillRect(wallLeft, WALL_Y_START, wallWidth, wallHeight);

    // --- BRICK RENDERING ---
    const brickH = 14;
    const brickW = wallWidth;

    // Draw the bricks and mortar lines
    this.graphics.lineStyle(1.5, 0x3d3833, 0.6); // Deeper mortar
    for (let by = WALL_Y_START; by < WALL_Y_END; by += brickH) {
      // Horizontal mortar
      this.graphics.beginPath();
      this.graphics.moveTo(wallLeft, by);
      this.graphics.lineTo(wallLeft + brickW, by);
      this.graphics.strokePath();

      // Vertical mortar (staggered)
      const row = Math.floor((by - WALL_Y_START) / brickH);
      const offset = (row % 2) * (brickW / 2);
      this.graphics.beginPath();
      this.graphics.moveTo(wallLeft + offset, by);
      this.graphics.lineTo(wallLeft + offset, by + brickH);
      this.graphics.strokePath();

      // --- AMBIENT OCCLUSION / LIGHTING ---
      // Top highlight per brick
      this.graphics.fillStyle(0xffffff, 0.1);
      this.graphics.fillRect(wallLeft, by + 1, brickW, 1.5);

      // Bottom shadow per brick
      this.graphics.fillStyle(0x000000, 0.15);
      this.graphics.fillRect(wallLeft, by + brickH - 2, brickW, 1.5);

      // Random stone depth variance
      const depthSeed = row * 13;
      if (this.seededRandom(depthSeed) > 0.6) {
        this.graphics.fillStyle(0x000000, 0.08); // Recessed shadow on whole stone
        this.graphics.fillRect(wallLeft, by, brickW, brickH);
      }
    }

    // --- TOP ORNAMENTAL CAP ---
    // A solid, slightly wider stone block that caps the top of the wall cleanly
    const capHeight = 16;
    const capWidth = wallWidth + 8; // Slightly overhangs the wall
    const capLeft = wallLeft - 4;

    // Top Cap Base
    this.graphics.fillStyle(wallColor, 1);
    this.graphics.fillRect(capLeft, WALL_Y_START - capHeight, capWidth, capHeight);

    // Top Cap Outline
    this.graphics.lineStyle(2, 0x3d3833, 1);
    this.graphics.strokeRect(capLeft, WALL_Y_START - capHeight, capWidth, capHeight);

    // Top Cap Highlight and Shadow for depth
    this.graphics.fillStyle(0xffffff, 0.15);
    this.graphics.fillRect(capLeft + 2, WALL_Y_START - capHeight + 2, capWidth - 4, 3);
    this.graphics.fillStyle(0x000000, 0.2);
    this.graphics.fillRect(capLeft + 2, WALL_Y_START - 4, capWidth - 4, 3);

    // Top Cap Inner Carving Details
    this.graphics.lineStyle(1.5, 0x2a2622, 0.6);
    this.graphics.strokeRect(capLeft + 6, WALL_Y_START - capHeight + 6, capWidth - 12, capHeight - 12);

    // --- BOTTOM ORNAMENTAL BASE ---
    // Identical solid block at the base

    // Bottom Cap Base
    this.graphics.fillStyle(wallColor, 1);
    this.graphics.fillRect(capLeft, WALL_Y_END - capHeight, capWidth, capHeight);

    // Bottom Cap Outline
    this.graphics.lineStyle(2, 0x3d3833, 1);
    this.graphics.strokeRect(capLeft, WALL_Y_END - capHeight, capWidth, capHeight);

    // Bottom Cap Highlight and Shadow
    this.graphics.fillStyle(0xffffff, 0.15);
    this.graphics.fillRect(capLeft + 2, WALL_Y_END - capHeight + 2, capWidth - 4, 3);
    this.graphics.fillStyle(0x000000, 0.2);
    this.graphics.fillRect(capLeft + 2, WALL_Y_END - 4, capWidth - 4, 3);

    // Bottom Cap Inner Carving Details
    this.graphics.lineStyle(1.5, 0x2a2622, 0.6);
    this.graphics.strokeRect(capLeft + 6, WALL_Y_END - capHeight + 6, capWidth - 12, capHeight - 12);

    // --- 5 DAMAGE STATES (<25%, 25-50%, 50-75%, >75%, 100%) ---
    // At 100%, skip drawing any damage
    if (hpPct < 1.0) {
      let severity = 0;
      if (hpPct <= 0.75 && hpPct > 0.5) severity = 1;      // Light: superficial cracks
      else if (hpPct <= 0.5 && hpPct > 0.25) severity = 2; // Moderate: wider cracks, some dust
      else if (hpPct <= 0.25) severity = 3;                // Heavy: massive fractures, black gouges

      // State 1: >75%, we still draw tiny hairline chips (Severity 0 logic essentially handles this baseline)

      // Base crack count increases directly as HP lowers
      const baseCracks = Math.max(2, Math.floor((1 - hpPct) * 15));
      const crackSeed = 42; // Constant seed so cracks don't jitter

      // Draw structural cracks
      for (let i = 0; i < baseCracks; i++) {
        const seedValue = crackSeed + i * 11;
        const cy = WALL_Y_START + 10 + (this.seededRandom(seedValue) * (wallHeight - 30));
        const cx = wallLeft + 2 + (this.seededRandom(seedValue + 1) * (wallWidth - 10));

        // Severity defines thickness and complexity of the crack branch
        const crackThick = severity >= 2 ? 2 : 1;
        const crackAlpha = severity >= 1 ? 0.8 : 0.5;
        this.graphics.lineStyle(crackThick, 0x1f1a16, crackAlpha); // Deep dark crack

        this.graphics.beginPath();
        this.graphics.moveTo(cx, cy);

        // Generate a jagged jagged line
        let px = cx, py = cy;
        const segments = 2 + severity;
        for (let s = 0; s < segments; s++) {
          const dx = (this.seededRandom(seedValue + 2 + s) - 0.5) * 12;
          const dy = (this.seededRandom(seedValue + 3 + s) * 10) + 4; // Tends downward
          px += dx;
          py += dy;
          this.graphics.lineTo(px, py);
        }
        this.graphics.strokePath();

        // If Heavy Damage, add black gouge marks/missing mortar chunks
        if (severity >= 3 && i % 2 === 0) {
          this.graphics.fillStyle(0x110f0d, 0.9);
          this.graphics.fillCircle(cx, cy, 3 + this.seededRandom(seedValue + 5) * 3);
        }
      }

      // If Moderate/Heavy (severity 2+), shade randomized broad damage patches covering the wall
      if (severity >= 2) {
        for (let i = 0; i < severity + 2; i++) {
          const py = WALL_Y_START + (this.seededRandom(crackSeed + 100 + i) * wallHeight);
          this.graphics.fillStyle(0x2a2118, 0.3); // Soot/rubble stain
          this.graphics.fillEllipse(wallLeft + wallWidth / 2, py, wallWidth - 4, 15 + severity * 10);
        }
      }
    }

    // Wall slot indicators
    for (const slot of this.slots) {
      if (!slot.occupied) {
        // Soft glowing dashed rings
        this.graphics.lineStyle(1.5, 0xffffff, 0.3);
        this.graphics.strokeCircle(slot.x, slot.y, 14);
        this.graphics.fillStyle(0xffffff, 0.05);
        this.graphics.fillCircle(slot.x, slot.y, 14);
      }
    }
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
